import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	UIEvent,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import styles from "./ScrollableVirtualList.module.less";
import { useResizeObserver } from "client/Hooks";
import classNames from "classnames";
import {
	VirtualList,
	VirtualListHandle,
	VirtualListProps
} from "../VirtualList/VirtualList";
import { LoadMoreIndicator } from "../LoadMoreIndicator/LoadMoreIndicator";
import { MobilePlusMenuSpacing } from "../VirtualList/MobilePlusMenuSpacing";
import { EndOfListSpacing } from "../VirtualList/EndOfListSpacing";

export type ScrollableVirtualListProps<TItem> = {
	isSmoothScrollingDisabled?: boolean;
	preserveScrollBarSpace?: boolean;
	onScroll?(args: ScrollableVirtualListScrollEventArgs): void;
}
	& VirtualListProps<TItem>;

export type ScrollableVirtualListHandle<TItem> = {
	scrollToTop: () => void;
	scrollToItem: (item: TItem) => void;
	scrollIntoView: (item: TItem) => void;
};


const ScrollableVirtualListInternal = forwardRef(
	<TItem,>(
		props: ScrollableVirtualListProps<TItem>,
		ref: Ref<ScrollableVirtualListHandle<TItem>>
	): ReactElement => {
		const {
			isSmoothScrollingDisabled,
			onScroll,
			preserveScrollBarSpace,
			...restOfProps
		} = props;

		const [scrollTop, setScrollTop] = useState<number>(0);

		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const rendererRef: RefObject<VirtualListHandle<TItem> | null> = useRef<VirtualListHandle<TItem>>(null);

		const { height: containerHeight } = useResizeObserver(containerRef);

		const scrollBottom: number = scrollTop + containerHeight;

		const scrollHandler = useCallback(
			(event: UIEvent<HTMLDivElement>) => {
				const scrollTop: number = event.currentTarget
					.scrollTop;

				if (!containerRef.current) {
					return;
				}

				setScrollTop(containerRef.current.scrollTop);

				onScroll?.({
					scrollPosition: scrollTop
				});
			},
			[onScroll]
		);

		useImperativeHandle(
			ref,
			(): ScrollableVirtualListHandle<TItem> => ({
				scrollToTop(): void {
					const containerElement: HTMLDivElement | null = containerRef.current;
					if (containerElement) {
						containerElement.scrollTop = 0;
					}
				},

				scrollToItem(item: TItem): void {
					const containerElement: HTMLDivElement | null = containerRef.current;
					const renderer = rendererRef.current;
					if (!containerElement || !renderer) {
						return;
					}

					const {
						top
					} = renderer.getItemPosition(item);

					containerElement.scrollTop = top;
				},
				scrollIntoView(item: TItem): void {
					const containerElement: HTMLDivElement | null = containerRef.current;
					const renderer = rendererRef.current;

					if (!containerElement || !renderer) {
						return;
					}

					const { top, height } = renderer.getItemPosition(item);
					const bottom: number = top + height;

					let offsetToScroll: number = 0;
					if (top < scrollTop) {
						offsetToScroll = top - scrollTop;
					} else if (scrollBottom < bottom) {
						offsetToScroll = bottom - scrollBottom;
					}

					if (offsetToScroll !== 0) {
						containerElement.scrollTop += offsetToScroll;
					}
				}
			}),
			[scrollTop, scrollBottom]
		);

		return (
			<div
				ref={containerRef}
				onScroll={scrollHandler}
				className={classNames(
					styles.root,
					{ [styles.root_preserveScrollBarSpace]: preserveScrollBarSpace },
					{ [styles.root_smooth]: !isSmoothScrollingDisabled }
				)}
			>
				<VirtualList
					{...restOfProps}
					ref={rendererRef}
				/>
			</div>
		);
	}
);
ScrollableVirtualListInternal.displayName = "ScrollableVirtualList";

export const ScrollableVirtualList = ScrollableVirtualListInternal as unknown as {
	<TItem>(props: ScrollableVirtualListProps<TItem> & { ref?: Ref<ScrollableVirtualListHandle<TItem>>; }): ReactElement;
	displayName: string;
	LoadMoreIndicator: typeof LoadMoreIndicator;
	MobilePlusMenuSpacing: typeof MobilePlusMenuSpacing;
	EndOfListSpacing: typeof EndOfListSpacing;
};

ScrollableVirtualList.LoadMoreIndicator = LoadMoreIndicator;
ScrollableVirtualList.MobilePlusMenuSpacing = MobilePlusMenuSpacing;
ScrollableVirtualList.EndOfListSpacing = EndOfListSpacing;

export type ScrollableVirtualListScrollEventArgs = {
	scrollPosition: number;
};